import { useMutation, useQuery } from "@tanstack/react-query";
import Card from "../../common/Card";
import useFormat from "../../util/useFormat";
import { apiDeleteSite, apiGetSites } from "../../api/ApiSites";
import { Content, ContentFixed, ContentScroll } from "../../common/Content";
import { HeaderMenu } from "../../common/HeaderMenu";
import clsx from "clsx";
import styles from "./Sites.module.scss";
import { Link, useSearchParams } from "react-router";
import { FabButton } from "../../common/FabButton";
import useAppContext from "../../useAppContext";
import { SiteStatusChip } from "../../common/SiteStatusChip";
import React, { useEffect, useState } from "react";
import SiteEditor from "./SiteEditor";
import { MODE_CREATE, MODE_EDIT } from "../../codes";
import { ItemActions } from "../../common/ItemActions";
import { DeleteModal } from "../../common/AppModal";
import useToast from "../../util/useToast";
import { useDebounce } from "use-debounce";
import Form from "react-bootstrap/esm/Form";
import { AppPagination } from "../../common/AppPagination";
import { formatDuration } from "../../util/util";

const Sites = () => {
  const { formatDate } = useFormat();
  const { getSiteStatus, sitesStatuses } = useAppContext();
  const { showToastSuccess } = useToast();

  const [searchParams, setSearchParams] = useSearchParams();

  const [searchInputValue, setSearchInputValue] = useState(searchParams.get("filter") || "");
  const [filterActiveValue, setFilterActiveValue] = useState(searchParams.get("active") || "");
  const [debouncedFilter] = useDebounce(searchInputValue, 200);

  const [siteEditor, setSiteEditor] = React.useState({ show: false, mode: MODE_CREATE, site: {} });
  const [deleteSiteModal, setDeleteSiteModal] = React.useState({ show: false, site: null });


  useEffect(() => {
    const filter = debouncedFilter.trim();

    if (filter === (searchParams.get("filter") || "") && filterActiveValue === (searchParams.get("active") || "")) {
      return;
    }

    const params = {};

    if (filter.length > 0) {
      params.filter = filter;
    }

    if (filterActiveValue.length) {
      params.active = filterActiveValue;
    }

    setSearchParams(params);
  }, [debouncedFilter, filterActiveValue]);

  const queryParams = { size: 50, page: +searchParams.get("page") || 0 };

  if (searchParams.get("filter")) {
    queryParams["filter"] = searchParams.get("filter");
  }

  if (searchParams.get("active")) {
    queryParams["active"] = searchParams.get("active") === "1";
  }

  const sitesQuery = useQuery({
    ...apiGetSites(queryParams),
    enabled: false
  });

  const deleteMutation = useMutation(apiDeleteSite());

  const sites = sitesQuery.data.content;
  const totalPages = sitesQuery.data.page.totalPages;
  const totalElements = sitesQuery.data.page.totalElements;

  return <Content>
    <ContentFixed>
      <HeaderMenu title="Tous les chantiers" />
    </ContentFixed>

    <ContentScroll paddingTop withFab>
      <div className={styles.searchBar}>
        <Form.Control
          type="text"
          className={styles.searchInput}
          placeholder="Chercher"
          value={searchInputValue}
          onChange={e => setSearchInputValue(e.target.value)}
        />

        <div className={styles.selectFilter}>
          <label>Chantiers actifs</label>

          <Form.Select className="form-control" onChange={e => setFilterActiveValue(e.target.value)}>
            <option value="1">Oui</option>
            <option value="0">Non</option>
            <option value="">Tous</option>
          </Form.Select>
        </div>

        {/*<DropdownFilter
          label="Statut"
          value={queryParams.status}
          onChange={value => {
            setSearchParams(prev => ({ ...prev, status: value }));
          }}
          choices={() => Promise.resolve(sitesStatuses.map(s => ({id: s.id, label: s.name})))}
        />*/}

        {/*<MultiSelectFilter label="Statut" values={sitesStatuses.map(s => ({value: s.id, label: s.name}))}/>*/}
      </div>

      <Card>
        <div className={clsx(styles.site, styles.header)}>
          <div className={styles.view}>
            <div className={styles.siteName}>Nom du chantier</div>
            <div className={styles.siteStatus}>Statut</div>
            <div className={styles.siteStartDate}>Date de début</div>
            <div className={styles.siteEndDate}>Date de fin</div>
            <div className={styles.siteTotalHours}>Heures</div>
          </div>
        </div>

        {sites.length === 0 && <div className={styles.empty}>Pas de résultats</div>}

        {sites.map(site => {
          const status = getSiteStatus(site.status);
          const siteColor = site.color.split("|");

          const siteActions = [
            {
              type: "item",
              icon: "edit",
              label: "Modifier",
              onClick: () => setSiteEditor({ show: true, mode: MODE_EDIT, site })
            },
            { type: "divider" },
            {
              type: "item",
              icon: "delete",
              label: "Supprimer",
              onClick: () => setDeleteSiteModal({ show: true, site })
            },
          ];

          return <div key={site.id} className={styles.site} style={{ borderColor: siteColor[0] }}>
            <Link to={"/sites/" + site.id} className={styles.view}>
              <span className={styles.siteName}>{site.name}</span>
              <span className={styles.siteStatus}><SiteStatusChip status={status} /></span>
              <span className={styles.siteStartDate}>{formatDate(site.dateStart)}</span>
              <span className={styles.siteEndDate}>{site.dateEnd ? formatDate(site.dateEnd) : "-"}</span>
              <span className={styles.siteTotalHours}>{formatDuration(site.totalHours || 0)}</span>
            </Link>

            <ItemActions actions={siteActions} />
          </div>;
        })}
      </Card>

      <Card padding>
        <AppPagination
          itemName="site"
          page={queryParams.page}
          totalElements={totalElements}
          totalPages={totalPages}
        />
      </Card>
    </ContentScroll>

    <FabButton onClick={() => setSiteEditor({ show: true, mode: MODE_CREATE, site: {} })} />

    <SiteEditor
      show={siteEditor.show}
      hide={() => setSiteEditor(prev => ({ ...prev, show: false }))}
      mode={siteEditor.mode}
      site={siteEditor.site}
      onSubmit={() => sitesQuery.refetch()}
    />

    <DeleteModal
      show={deleteSiteModal.show}
      title="Supprimer le chantier"
      onCancel={() => setDeleteSiteModal({ show: false })}
      onSubmit={() => {
        return deleteMutation.mutateAsync(deleteSiteModal.site.id)
          .then(() => sitesQuery.refetch())
          .then(() => {
            showToastSuccess("Chantier supprimé");

            setDeleteSiteModal({ show: false });
          });
      }}
      cancelLabel="Annuler"
      submitLabel="Supprimer"
    >
      Etes-vous sûr de vouloir supprimer ce chantier ?
    </DeleteModal>
  </Content>;
};

export default Sites;